/**
 Calculate the final total including tip tax service fee bulk order fee.
 **/
import Dinero from 'dinero.js';

const toDinero = (value) => {
  const cents = Math.round((parseFloat(value) || 0) * 100);
  return Dinero({ amount: cents, currency: 'USD' });
};

export const calculateCartTotal = (cartTotal, tipPercentValue, tipPercentType, tax, serviceFee, bulkOrderFee) => {
  const base = toDinero(cartTotal);
  // Tip calculation (percentage of base)
  let tip = Dinero({ amount: 0, currency: 'USD' });
  if (tipPercentType === "percent") {
    const tipPercentNum = parseFloat(tipPercentValue) || 0;
    tip = base.percentage(tipPercentNum);
  }
  else if (tipPercentType === "amount") {
    const tipAmount = parseFloat(tipPercentValue) || 0;
    tip = toDinero(tipAmount);
  }

  // Tax calculation
  let taxAmount = Dinero({ amount: 0, currency: 'USD' });
  if (tax) {
    const taxValue = parseFloat(tax.value) || 0;
    if (tax.type === "percentage") {
      taxAmount = base.percentage(taxValue);
    } else if (tax.type === "amount") {
      taxAmount = toDinero(taxValue);
    }
  }

  // Bulk order fee calculation
  let bulkOrderAmount = Dinero({ amount: 0, currency: 'USD' });
  if (bulkOrderFee) {
    const threshold = parseFloat(bulkOrderFee.threshold) || 0;
    if (parseFloat(cartTotal) >= threshold) {
      const bulkOrderValue = parseFloat(bulkOrderFee.value) || 0;
      if (bulkOrderFee.type === "percentage") {
        bulkOrderAmount = base.percentage(bulkOrderValue);
      } else if (bulkOrderFee.type === "amount") {
        bulkOrderAmount = toDinero(bulkOrderValue);
      }
    }
  }

  // Service fee calculation
  let serviceFeeAmount = Dinero({ amount: 0, currency: 'USD' });
  if (serviceFee && serviceFee.is_active) {
    const serviceFeeValue = parseFloat(serviceFee.value) || 0;
    if (serviceFee.type === "percentage") {
      serviceFeeAmount = base.percentage(serviceFeeValue);
    } else if (serviceFee.type === "amount") {
      serviceFeeAmount = toDinero(serviceFeeValue);
    }
  }

  // Total = base + tip + tax + bulk + service
  const total = base
    .add(tip)
    .add(taxAmount)
    .add(bulkOrderAmount)
    .add(serviceFeeAmount);


  // console.log("base", base.toFormat('$0,0.00'));
  // console.log("tip", tip.toFormat('$0,0.00'));
  // console.log("taxamount", taxAmount.toFormat('$0,0.00'));
  // console.log("bulk", bulkOrderAmount.toFormat('$0,0.00'));
  // console.log("service", serviceFeeAmount.toFormat('$0,0.00'));

  return {
    total: total.toFormat('0,0.00'), // numeric value in dollars
    taxAmount: taxAmount.toFormat('0,0.00'),
    tip_amount: tip.toFormat('0,0.00'),
    bulkFee: bulkOrderAmount.toFormat('0,0.00'),
    service_Fee: serviceFeeAmount.toFormat('0,0.00'),
    totalRaw: total.toFormat('0.00'),
    taxAmountRaw: taxAmount.toFormat('0.00'),
    tip_amountRaw: tip.toFormat('0.00'),
    bulkFeeRaw: bulkOrderAmount.toFormat('0.00'),
    service_FeeRaw: serviceFeeAmount.toFormat('0.00')
  };
};

export function detectMetadataChanges(cart, metadata, previousRestaurantInfo) {
  if (!metadata || Object.keys(metadata).length === 0) return null;
  const itemsRemoved = [];
  const itemsUpdated = [];
  const restaurantInfoUpdated = [];

  const menuMap = {};

  // Flatten menu into map
  metadata.menu_items?.forEach(cat => {
    cat.menu_details.forEach(item => {
      menuMap[item.menu_id] = item;
    });
  });

  // ----------------------------------------------------------
  // RESTAURANT INFO CHANGES
  // ----------------------------------------------------------
  const currentInfo = metadata.restaurant_info;

  const compareField = (key, oldValue, newValue) => {
    if (String(oldValue) !== String(newValue)) {
      restaurantInfoUpdated.push({
        field: key,
        oldValue: oldValue,
        newValue: newValue
      });
    }
  };

  if (previousRestaurantInfo) {
    compareField("name", previousRestaurantInfo.name, currentInfo.name);
    compareField("address", previousRestaurantInfo.address, currentInfo.address);
    compareField("logo", previousRestaurantInfo.logo, currentInfo.logo);
    compareField("timezone", previousRestaurantInfo.timezone, currentInfo.timezone);

    // tax
    compareField(
      "tax",
      previousRestaurantInfo.tax?.value,
      currentInfo.tax?.value
    );

    // service fee
    compareField(
      "service_fee",
      previousRestaurantInfo.service_fee?.value,
      currentInfo.service_fee?.value
    );

    // bulk fees
    compareField(
      "bulk_order_fees",
      previousRestaurantInfo.bulk_order_fees?.value,
      currentInfo.bulk_order_fees?.value
    );
  }

  // ----------------------------------------------------------
  // ITEM REMOVED / UPDATED
  // ----------------------------------------------------------
  cart.forEach(cartItem => {
    const latest = menuMap[cartItem.item_id];

    // ITEM REMOVED OR OUT OF STOCK
    if (!latest || latest.menu_in_stock === false) {
      itemsRemoved.push({
        id: cartItem.item_id,
        name: cartItem.item_name,
        reason: latest ? "Out of Stock" : "Removed from menu"
      });
      return;
    }

    const update = {
      id: cartItem.item_id,
      name: cartItem.item_name,
      oldPrice: Number(cartItem.item_base_price),
      newPrice: Number(latest.menu_item_price),
      priceChanged: false,
      removedModifiers: [],
      modifierPriceChanges: [],
      modifierQtyChanges: [],
      modifierNameChanges: [],
      nameChanged: false
    };
    // compare name change
    if (cartItem.item_name !== latest.menu_item_name) {
      update.name = latest.menu_item_name;
      update.nameChanged = true;
    }
    // Price change
    if (Number(cartItem.item_base_price) !== Number(latest.menu_item_price)) {
      update.priceChanged = true;
    }
    //console.log("cartcustomizations", cartItem.customizations);
    //console.log("latest", latest);
    // Deep modifier comparison
    if (cartItem.customizations) {
      cartItem.customizations.forEach(cust => {
        const group = latest.options?.find(g => g.id === cust.groupId);
        console.log("group", group);
        if (!group) {
          update.removedModifiers.push({
            name: cust.name,
            reason: "Modifier group removed"
          });
          return;
        }

        const option = group.option.find(o => o.opt_id === cust.optionId);
        //console.log("option", option);
        if (!option) {
          update.removedModifiers.push({
            name: cust.name,
            reason: "Option removed"
          });
          return;
        }
        // Modifier name change
        if (cust.name !== option.name) {
          update.modifierNameChanges.push({
            name: cust.name,
            oldName: cust.name,
            newName: option.name
          });
        }
        // Modifier price changed
        if (Number(option.price) !== Number(cust.price)) {
          console.log("Modifier price changed", cust.name, cust.price, option.name, option.price);
          update.modifierPriceChanges.push({
            name: cust.name,
            oldPrice: cust.price,
            newPrice: Number(option.price)
          });
        }
        // modifier qty change
        if ((group.allow_multiple_selection === true) && (Number(group.item_limit) !== 0) && (Number(group.item_limit) < Number(cust.qty))) {
          console.log("Modifier qty changed", cust.name, cust.qty, option.name, group.item_limit);
          update.modifierQtyChanges.push({
            name: cust.name,
            oldQty: cust.qty,
            newQty: group.item_limit
          });
        }

        // Sub modifiers
        cust.subModifiers?.forEach(sub => {
          const subGroup = option.sub_options?.find(g => g.id === sub.groupId);
          //console.log("subGroup", subGroup);
          if (!subGroup) {
            update.removedModifiers.push({
              name: sub.name,
              reason: "Sub option removed"
            });
            return;
          }

          const subOpt = subGroup.option.find(o => o.opt_id === sub.optionId);
          //console.log("subOpt", subOpt);
          if (!subOpt) {
            update.removedModifiers.push({
              name: sub.name,
              reason: "Sub option removed"
            });
            return;
          }
          //Sub modifier name change
          if (sub.name !== subOpt.name) {
            update.modifierNameChanges.push({
              name: sub.name,
              oldName: sub.name,
              newName: subOpt.name
            });
          }
          //Sub modifier price change
          if (Number(subOpt.price) !== Number(sub.price)) {
            console.log("Sub modifier price changed", sub.name, sub.price, subOpt.opt_name, subOpt.price);
            update.modifierPriceChanges.push({
              name: sub.name,
              oldPrice: sub.price,
              newPrice: Number(subOpt.price)
            });
          }
          //Sub modifier qty change
          if ((subGroup.allow_multiple_selection === true) && (Number(subGroup.item_limit) !== 0) && (Number(subGroup.item_limit) < Number(sub.qty))) {
            console.log("Sub modifier qty changed", sub.name, sub.qty, subOpt.name, subGroup.item_limit, subGroup.allow_multiple_selection);
            update.modifierQtyChanges.push({
              name: sub.name,
              oldQty: sub.qty,
              newQty: subGroup.item_limit
            });
          }
          //console.log("QTYCHANGES", update.modifierQtyChanges);
        });
      });
    }

    if (
      update.nameChanged ||
      update.priceChanged ||
      update.removedModifiers.length > 0 ||
      update.modifierQtyChanges.length > 0 ||
      update.modifierPriceChanges.length > 0 ||
      update.modifierNameChanges.length > 0
    ) {
      itemsUpdated.push(update);
    }
  });

  // Track available (in-stock) items
  const availableCartItems = cart.filter(c => {
    const latest = menuMap[c.item_id];
    return latest && latest.menu_in_stock !== false;
  });

  // ----------------------------------------------------------
  // TOTALS UPDATED
  // ----------------------------------------------------------
  let totalsUpdated = null;

  // If cart becomes empty, do NOT calculate totals
  if (availableCartItems.length > 0) {
    let newSubtotal = 0;

    availableCartItems.forEach(c => {
      const latest = menuMap[c.item_id];
      newSubtotal += Number(latest.menu_item_price) * c.quantity;
    });

    const oldSubtotal = cart.reduce((sum, c) => sum + (Number(c.item_base_price) * c.quantity), 0);
    const taxRate = Number(metadata.restaurant_info.tax.value) / 100;

    const oldTax = oldSubtotal * taxRate;
    const newTax = newSubtotal * taxRate;

    const oldTotal = oldSubtotal + oldTax;
    const newTotal = newSubtotal + newTax;

    // Round
    const totalsOld = {
      oldSubtotal: Number(oldSubtotal.toFixed(2)),
      oldTax: Number(oldTax.toFixed(2)),
      oldTotal: Number(oldTotal.toFixed(2)),
    };

    const totalsNew = {
      newSubtotal: Number(newSubtotal.toFixed(2)),
      newTax: Number(newTax.toFixed(2)),
      newTotal: Number(newTotal.toFixed(2)),
    };

    // If totals did NOT change, do NOT include totalsUpdated
    const totalsChanged =
      totalsOld.oldSubtotal !== totalsNew.newSubtotal ||
      totalsOld.oldTax !== totalsNew.newTax ||
      totalsOld.oldTotal !== totalsNew.newTotal;

    totalsUpdated = totalsChanged
      ? {
        oldSubtotal: totalsOld.oldSubtotal,
        newSubtotal: totalsNew.newSubtotal,
        oldTax: totalsOld.oldTax,
        newTax: totalsNew.newTax,
        oldTotal: totalsOld.oldTotal,
        newTotal: totalsNew.newTotal
      }
      : null;
  }

  const cartEmptied = cart.length > 0 && availableCartItems.length === 0;

  const nothingChanged =
    restaurantInfoUpdated.length === 0 &&
    itemsRemoved.length === 0 &&
    itemsUpdated.length === 0 &&
    totalsUpdated === null;

  if (nothingChanged) return null;

  return {
    restaurantInfoUpdated,
    itemsRemoved,
    itemsUpdated,
    totalsUpdated,
    cartEmptied
  };
}


